import { motion, useReducedMotion } from "framer-motion";

import { education } from "../../../content";
import { educationMark } from "../../../utils/educationLogos";
import type { EducationEntry } from "../../../types/content";
import styles from "./education.module.css";

/**
 * ============================================================================
 * Education — direct consumer of the content layer.
 * ============================================================================
 *
 * Every fact in this section comes from `src/content/data/education.json`
 * through the loader (Constitution V.4 — the section is rebuilt, not adapted,
 * so there is no legacy shape a bridge would serve). To change what it says,
 * edit the JSON; there is no copy in this file.
 *
 * ----------------------------------------------------------------------------
 * REDESIGNED 2026-08-30 (D-095): A TIMELINE, IN EXPERIENCE'S LANGUAGE
 * ----------------------------------------------------------------------------
 * PRESENTATION ONLY. Every string, the ordering rule and the reveal animation
 * are unchanged; `education.json` was not touched. What changed is the shape:
 * a 2x2 card grid became a chronological spine with a node per degree,
 * alternating around the centre above 1024px and running down a left rail
 * below it — the same geometry, breakpoint and tokens as `Experience_v2`.
 *
 * The argument for the change is cohesion rather than novelty. This site sets
 * a career on a timeline in one section and the education that underwrites it
 * in a grid two sections later, and a reader scrolling between them meets two
 * different ways of saying "these happened in an order". Four degrees over ten
 * years IS a sequence, and the grid was the one place on the page where a
 * sequence was drawn as a set.
 *
 * WHAT THE NODE CARRIES — THE INSTITUTION'S MARK, since D-097. It carried
 * derived initials before that, and the comment here argued at length that a
 * crest could not work: D-049 found the four in the repo wordmark-heavy
 * (MAKAUT's carries three lines of type), unreadable at tile size, and dark ink
 * on white, "so each would need an opaque plate invented to survive the dark
 * theme".
 *
 * EVERY ONE OF THOSE OBSERVATIONS WAS TRUE AND THE CONCLUSION DID NOT FOLLOW.
 * Two of them are properties of the FILES, not of the marks inside them, and
 * the wordmarks have been cropped off against the ink rather than by eye
 * (`logotool.mjs`, D-097 — MAKAUT's shield starts at y=174, four rows below
 * where "WEST BENGAL" ends). The third is a property of the SETTING: "dark ink
 * on white" is answered by giving the artwork the white ground it was drawn
 * for. The plate D-049 called invented is `.logo-plate` in index.css, and
 * inventing it was the correct move rather than a reason to decline.
 *
 * The initials are gone from this section rather than kept as a fallback,
 * because `educationLogos.ts` throws at module load if any entry lacks a mark —
 * so a fallback here could never render, and unreachable code that looks like a
 * safety net is worse than none. `Experience_v2` keeps its own initials, where
 * they are the real state: no company logo exists on this site yet.
 *
 * THE YEAR MOVES TO ITS OWN COLUMN ABOVE 1024px and stays inside the card
 * below it — Experience's `.aside` / `.metaInline` pair, for its reason: the
 * date is chronology, the spine is chronology, and putting the two beside each
 * other is what makes the alternation readable. It is one element in the DOM
 * with a CSS-hidden twin, not two copies of a fact.
 *
 * ----------------------------------------------------------------------------
 * Kept from the D-049 revision, because none of it was the problem:
 * ----------------------------------------------------------------------------
 * The card material is `.card-shell` / `.card-face` (D-061, index.css) — the
 * site-wide card, not a copy. Every colour is a token from
 * `global/theme/tokens.css`; no token was added, which is again the test of
 * whether the existing roles were the right ones. There is still NO intro
 * paragraph, for the reason Services has none: a standfirst is copy, and copy
 * about the author's education is content — it belongs in the layer or in an
 * owner ruling, not in a component.
 *
 * The three-step hierarchy inside the card is by colour AND by size, because
 * colour alone collapses for a reader with a contrast or colour deficiency.
 * The study-centre note is still set apart by a hairline rather than by a
 * fourth colour: `--text-faint` is tuned against `--surface-card` and this face
 * paints `--surface`, and D-045 is the lesson that a token cleared for AA is
 * cleared against a backdrop rather than in general.
 */

/** "2015 – 2018", collapsing to a single year when a programme starts and ends in one. */
const yearRange = (entry: EducationEntry): string =>
	entry.startYear === entry.endYear ? String(entry.startYear) : `${entry.startYear} – ${entry.endYear}`;

/**
 * Newest first, matching Experience_v2 and /stories. Sorted here rather than in
 * the JSON so the content file stays canonical (the D-017 precedent: display
 * order is positioning, and positioning lives in the component). The `startYear`
 * tiebreak settles two programmes finishing the same year in favour of the
 * longer one.
 */
const orderedEducation: EducationEntry[] = [...education].sort(
	(a: EducationEntry, b: EducationEntry) => b.endYear - a.endYear || b.startYear - a.startYear
);

const EducationEntryRow = ({ entry, index }: { entry: EducationEntry; index: number }): JSX.Element => {
	const reduceMotion = useReducedMotion();
	/* Odd entries sit right of the spine, even ones left — Experience's
	   alternation, so the two sections mirror rather than merely resemble. */
	const flipped = index % 2 === 1;
	const range = yearRange(entry);

	return (
		<motion.li
			className={`${styles.item} ${flipped ? styles.flipped : ""}`}
			initial={reduceMotion ? false : { opacity: 0, y: 28 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, amount: 0.2 }}
			transition={{ duration: 0.55, delay: Math.min(index * 0.06, 0.24), ease: [0.22, 1, 0.36, 1] }}
		>
			{/* ---------- spine node ---------- */}
			<div className={styles.rail}>
				{/* `aria-hidden` ON THE WHOLE NODE, and the mark is `alt=""`. The crest
				    is a visual marker for an institution the card names in full two
				    elements away, so a screen reader announcing a logo before the
				    card's own "Indian Institute of Management (IIM) Kashipur" would
				    read one fact twice. Same argument that made the initials
				    aria-hidden before them, and the same one D-095 used for the
				    workshop glimpse thumbnails. */}
				<span className={styles.node} aria-hidden="true">
					<span className="logo-plate">
						<img src={educationMark(entry.id)} alt="" loading="lazy" decoding="async" />
					</span>
				</span>
			</div>

			{/* ---------- the card ---------- */}
			<div className={`${styles.panel} card-shell`}>
				<article className={`${styles.panelFace} card-face`}>
					{/* THE IN-CARD YEAR, hidden above 1024px where the aside carries
					    it. One fact, two placements, never two elements both
					    visible. */}
					<span
						className={`${styles.metaInline} pill-tint tabular-nums mb-3 inline-block border-[var(--cat-indigo-border)] bg-[var(--cat-indigo-fill)] text-[var(--cat-indigo-text)]`}
					>
						{range}
					</span>

					<h3 className="text-lg font-bold leading-snug text-[var(--text-strong)] sm:text-xl">
						{entry.degree}
					</h3>
					<p className="mt-1.5 text-sm font-medium text-[var(--text-secondary)] sm:text-base">
						{entry.institution}
					</p>
					<p className="mt-3 text-sm text-[var(--text-muted)]">{entry.field}</p>

					{entry.notes && (
						<p className="mt-4 border-t border-[var(--border-soft)] pt-3 text-xs text-[var(--text-muted)]">
							{entry.notes}
						</p>
					)}
				</article>
			</div>

			{/* ---------- desktop-only year column ---------- */}
			<div className={styles.aside}>
				<span className="pill-tint tabular-nums border-[var(--cat-indigo-border)] bg-[var(--cat-indigo-fill)] text-[var(--cat-indigo-text)]">
					{range}
				</span>
			</div>
		</motion.li>
	);
};

/**
 * Renders nothing when the layer carries no entries — Article II.4 forbids an
 * empty section, and that has to be a property of the component rather than of
 * whoever edits the JSON next (the HighlightedStories precedent).
 */
const Education_v2 = (): JSX.Element | null => {
	if (!orderedEducation.length) return null;

	return (
		<section className={`section-shell ${styles.section}`} id="education" aria-labelledby="education-heading">
			<div className="section-rail">
				{/* Owner ruling 2026-08-19 (D-063): every section carries an eyebrow. */}
				<p className="section-eyebrow">Academic Foundation</p>

				<h2 id="education-heading" className="heading">
					<span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]">
						Education
					</span>
				</h2>

				<ol className={`${styles.timeline} mt-12`}>
					{orderedEducation.map((entry: EducationEntry, index: number) => (
						<EducationEntryRow key={entry.id} entry={entry} index={index} />
					))}
				</ol>
			</div>
		</section>
	);
};

export default Education_v2;
